import { samplePath } from "./splitPathToRails";
const SATIN_THIN_MAX_MM = 2;
const SATIN_WIDE_MIN_MM = 4;
const _median = (arr) => {
  if (!arr.length) return null;
  const s = [...arr].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

// Estimate a satin column's rail-to-rail width (in mm) from its two rail paths.
// Samples both rails, takes the nearest-opposite-rail distance for each sample
// on rail0, and returns the median so a few end-cap outliers (where the rails
// meet) don't skew the result. `mmPerUnit` converts SVG user units -> mm.
// Returns null when it can't measure (so callers fall back to a safe default).
export const measureSatinColumnWidthMm = (rail0D, rail1D, mmPerUnit) => {
  try {
    if (!rail0D || !rail1D || !mmPerUnit) return null;
    const a = samplePath(rail0D, 60);
    const b = samplePath(rail1D, 60);
    if (a.length < 2 || b.length < 2) return null;
    const dists = [];
    for (const p of a) {
      let best = Infinity;
      for (const q of b) {
        const dx = p.x - q.x;
        const dy = p.y - q.y;
        const d2 = dx * dx + dy * dy;
        if (d2 < best) best = d2;
      }
      if (best < Infinity) dists.push(Math.sqrt(best));
    }
    const medUnits = _median(dists);
    if (medUnits == null) return null;
    return medUnits * mmPerUnit;
  } catch {
    return null;
  }
};

export function satinUnderlayAttributes(settings, widthMm) {
  const attrs = {};
  const isThin = widthMm != null && widthMm <= SATIN_THIN_MAX_MM;
  const isWide = widthMm != null && widthMm > SATIN_WIDE_MIN_MM;
  const underlayEnabled = settings.fill_underlay !== false;
  const underlayProfile = settings.satin_underlay_profile || "auto";
  const useCenter = underlayEnabled;
  const useContour =
    underlayEnabled &&
    (underlayProfile === "center_edge" ||
      underlayProfile === "full" ||
      (underlayProfile === "auto" && !isThin));
  const useZigzag =
    underlayEnabled &&
    (underlayProfile === "full" || (underlayProfile === "auto" && isWide));
  // When width is unknown, Auto uses the safe medium profile. Center walk
  // runs the mid-line of every enabled profile for registration.
  attrs["inkstitch:center_walk_underlay"] = useCenter ? "true" : "false";
  attrs["inkstitch:center_walk_underlay_stitch_length_mm"] = "2.5";

  if (useContour) {
    attrs["inkstitch:contour_underlay"] = "true";
    // The UI exposes a fixed inset in mm; do not add a hidden percentage.
    attrs["inkstitch:contour_underlay_inset_mm"] = String(
      settings.contour_underlay_inset_mm ?? 0.4
    );
    attrs["inkstitch:contour_underlay_inset_percent"] = "0";
    attrs["inkstitch:contour_underlay_stitch_length_mm"] = "2.5";
  } else {
    attrs["inkstitch:contour_underlay"] = "false";
  }

  if (useZigzag) {
    attrs["inkstitch:zigzag_underlay"] = "true";
    attrs["inkstitch:zigzag_underlay_spacing_mm"] = String(
      settings.zigzag_underlay_spacing_mm || 3
    );
    // Inset left at Ink/Stitch's default (half of the contour inset, fixed
    // + proportional) so the zigzag points fall between the contour line and
    // the satin edge rather than out at the edge.
  } else {
    attrs["inkstitch:zigzag_underlay"] = "false";
  }
  return attrs;
}
export function svgMillimetersPerUnit(svg) {
  const vb = String(svg.attributes?.viewBox || "")
    .trim()
    .split(/[ ,]+/)
    .map(Number);
  const mm = (value) => {
    const match = String(value || "").match(/^([0-9.]+)mm$/);
    return match ? Number(match[1]) : null;
  };
  const w = mm(svg.attributes?.width),
    h = mm(svg.attributes?.height);
  return vb.length === 4 && vb[2] > 0 && vb[3] > 0 && w && h
    ? (w / vb[2] + h / vb[3]) / 2
    : null;
}
